import type { JSONContent } from '@tiptap/react'

interface TranscriptionWord {
  w: string
  s: number
  e: number
}

interface DialogLine {
  characterId: string
  characterName: string
  text: string
  tone?: string
}

export type ClipAudioState = 'tts-ready' | 'project-tts' | 'native-audio' | 'no-audio'

export interface ClipData {
  _id: string
  title?: string
  promptSummary?: string
  duration: number
  sceneLabel?: string
  dialog?: DialogLine[]
  transcription?: {
    words: TranscriptionWord[]
    text: string
    language?: string
  }
  transcriptionStatus?: string
  ttsStatus?: string
  nativeAudioEnabled?: boolean
  charactersInClip?: string[]
  videoUrl?: string
  videoStatus?: string
  staleReasons?: string[]
  generationMode?: string
  trimStart?: number
  trimEnd?: number
  speed?: number
  orderIndex?: number
}

export function getClipAudioState(clip: ClipData, projectTtsStatus?: string): ClipAudioState {
  if (clip.nativeAudioEnabled) return 'native-audio'
  if (projectTtsStatus === 'ready' || projectTtsStatus === 'stale') return 'project-tts'
  if (clip.ttsStatus === 'ready') return 'tts-ready'
  return 'no-audio'
}

export interface CharacterData {
  id: string
  name: string
}

function buildWordContent(words: TranscriptionWord[], clipOffset: number): JSONContent[] {
  if (!words.length) return []

  return words.map((word, i) => ({
    type: 'text',
    text: word.w + (i < words.length - 1 ? ' ' : ''),
    marks: [
      {
        type: 'wordMark',
        attrs: {
          start: clipOffset + word.s,
          end: clipOffset + word.e,
        },
      },
    ],
  }))
}

export interface TtsTimestamp {
  word: string
  startMs: number
  endMs: number
  inputIndex: number
}

/** Normalize text for fuzzy word matching: lowercase, strip punctuation */
function normalizeWord(w: string): string {
  return w.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '')
}

/**
 * Match a dialog line's text against TTS timestamps starting from cursor.
 * Returns word marks and the new cursor position.
 */
function matchDialogToTimestamps(
  dialogText: string,
  timestamps: TtsTimestamp[],
  cursor: number,
): { content: JSONContent[]; newCursor: number } {
  if (!dialogText.trim() || cursor >= timestamps.length) {
    return {
      content: dialogText ? [{ type: 'text', text: dialogText }] : [],
      newCursor: cursor,
    }
  }

  const dialogWords = dialogText.split(/\s+/).filter(w => w.length > 0)
  const content: JSONContent[] = []
  let tsCursor = cursor

  for (let i = 0; i < dialogWords.length; i++) {
    const dWord = dialogWords[i]
    const dNorm = normalizeWord(dWord)

    // Look ahead in timestamps for a match (allow small gaps for punctuation-only differences)
    let matched = false
    for (let look = 0; look < 3 && tsCursor + look < timestamps.length; look++) {
      const tsNorm = normalizeWord(timestamps[tsCursor + look].word)
      if (tsNorm === dNorm || (dNorm.length > 2 && tsNorm.startsWith(dNorm)) || (tsNorm.length > 2 && dNorm.startsWith(tsNorm))) {
        // Skip any unmatched gap timestamps
        tsCursor += look
        const ts = timestamps[tsCursor]
        content.push({
          type: 'text',
          text: dWord + (i < dialogWords.length - 1 ? ' ' : ''),
          marks: [{
            type: 'wordMark',
            attrs: {
              start: ts.startMs / 1000,
              end: ts.endMs / 1000,
            },
          }],
        })
        tsCursor++
        matched = true
        break
      }
    }
    if (!matched) {
      // No timestamp match — emit plain text
      content.push({ type: 'text', text: dWord + (i < dialogWords.length - 1 ? ' ' : '') })
    }
  }

  return { content, newCursor: tsCursor }
}

/**
 * Build approximate word marks by distributing words proportionally across clip duration.
 * Uses character-length weighting so longer words get more time.
 */
function buildApproxWordContent(text: string, clipOffset: number, clipDuration: number): JSONContent[] {
  const words = text.split(/\s+/).filter(w => w.length > 0)
  if (!words.length) return []

  const totalChars = words.reduce((sum, w) => sum + w.length, 0)
  let elapsed = 0

  return words.map((word, i) => {
    const fraction = word.length / totalChars
    const wordDuration = clipDuration * fraction
    const start = clipOffset + elapsed
    const end = start + wordDuration
    elapsed += wordDuration

    return {
      type: 'text',
      text: word + (i < words.length - 1 ? ' ' : ''),
      marks: [{
        type: 'wordMark',
        attrs: { start, end },
      }],
    }
  })
}

function buildDialogBlocks(
  clipId: string,
  dialog: DialogLine[],
  timestamps?: TtsTimestamp[],
  tsCursor?: { value: number },
  clipOffset?: number,
  clipDuration?: number,
): JSONContent[] {
  return dialog.map((line, index) => {
    let content: JSONContent[] | undefined
    if (line.text && timestamps && tsCursor) {
      const result = matchDialogToTimestamps(line.text, timestamps, tsCursor.value)
      content = result.content.length > 0 ? result.content : undefined
      tsCursor.value = result.newCursor
    } else if (line.text && clipOffset !== undefined && clipDuration) {
      // No exact timestamps — use proportional approximation
      content = buildApproxWordContent(line.text, clipOffset, clipDuration)
    } else if (line.text) {
      content = [{ type: 'text', text: line.text }]
    }

    return {
      type: 'dialogBlock',
      attrs: {
        characterId: line.characterId,
        characterName: line.characterName,
        clipId,
        sourceType: 'dialog' as const,
        audioStale: false,
        dialogIndex: index,
      },
      content,
    }
  })
}

function buildTranscriptionBlock(
  clipId: string,
  transcription: { words: TranscriptionWord[]; text: string },
  clipOffset: number,
  characterId?: string,
  characterName?: string,
): JSONContent {
  const wordContent = buildWordContent(transcription.words, clipOffset)
  const fallbackContent = transcription.text
    ? [{ type: 'text' as const, text: transcription.text }]
    : undefined

  return {
    type: 'dialogBlock',
    attrs: {
      characterId: characterId || '',
      characterName: characterName || '',
      clipId,
      sourceType: 'transcription' as const,
      audioStale: false,
    },
    content: wordContent.length > 0 ? wordContent : fallbackContent || undefined,
  }
}

function buildEmptyBlock(clipId: string): JSONContent {
  return {
    type: 'dialogBlock',
    attrs: {
      characterId: '',
      characterName: '',
      clipId,
      sourceType: 'empty' as const,
      audioStale: false,
    },
  }
}

export function buildDocumentFromClips(
  clips: ClipData[],
  characters: CharacterData[],
  projectTtsStatus?: string,
  projectTtsTimestamps?: TtsTimestamp[],
): JSONContent {
  const content: JSONContent[] = []
  let lastSceneLabel = ''
  let cumulativeTime = 0

  // Shared cursor for sequential TTS word matching across clips
  const tsCursor = projectTtsTimestamps?.length ? { value: 0 } : undefined

  for (let i = 0; i < clips.length; i++) {
    const clip = clips[i]
    const clipOffset = cumulativeTime
    const sceneLabel = clip.sceneLabel || ''
    const showScene = sceneLabel && sceneLabel !== lastSceneLabel
    if (sceneLabel) lastSceneLabel = sceneLabel

    const audioStatus = clip.ttsStatus === 'ready'
      ? 'ready'
      : clip.ttsStatus === 'generating'
        ? 'generating'
        : clip.nativeAudioEnabled
          ? 'native'
          : 'none'

    const clipAudioState = getClipAudioState(clip, projectTtsStatus)
    const isStale = (clip.staleReasons || []).length > 0
    const hasVideo = clip.videoStatus === 'completed' && !!clip.videoUrl

    content.push({
      type: 'sceneBlock',
      attrs: {
        clipId: clip._id,
        sceneLabel: showScene ? sceneLabel : clip.promptSummary || clip.title || `Clip ${i + 1}`,
        clipIndex: i,
        duration: clip.duration,
        clipStartTime: clipOffset,
        hasAudio: !!(clip.nativeAudioEnabled || clip.ttsStatus === 'ready'),
        audioStatus,
        clipAudioState,
        isStale,
        hasVideo,
        generationMode: clip.generationMode || '',
      },
    })

    const hasTranscription =
      clip.transcriptionStatus === 'ready' &&
      clip.transcription &&
      (clip.transcription.words.length > 0 || clip.transcription.text)

    const hasDialog = clip.dialog && clip.dialog.length > 0

    if (hasTranscription && clip.transcription) {
      const firstCharId = clip.charactersInClip?.[0]
      const firstChar = firstCharId
        ? characters.find((c) => c.id === firstCharId)
        : undefined
      content.push(
        buildTranscriptionBlock(
          clip._id,
          clip.transcription,
          clipOffset,
          firstChar?.id,
          firstChar?.name,
        ),
      )
    }

    if (hasDialog && clip.dialog) {
      content.push(...buildDialogBlocks(clip._id, clip.dialog, projectTtsTimestamps, tsCursor, clipOffset, clip.duration))
    }

    if (!hasTranscription && !hasDialog) {
      content.push(buildEmptyBlock(clip._id))
    }

    cumulativeTime += clip.duration || 5
  }

  if (content.length === 0) {
    content.push({ type: 'paragraph' })
  }

  return { type: 'doc', content }
}
